import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import styled from 'styled-components';

/* A shared row list for blogs, used by both the home section and the
   /blogs page so the two can never drift apart.

   Rows rather than cards: a card puts a box around every item, which at this
   density reads as a grid of unrelated objects. A rule between rows carries
   the same separation with far less furniture, and leaves room for the
   description without the row becoming a panel. */
const List = styled.ul`
  ${({ theme }) => theme.mixins.resetList};
  width: 100%;
  border-top: 0px solid var(--border);
`;

const Row = styled.li`
  border-bottom: 0px solid var(--border);
  padding: var(--space-3) 0;

  .inner {
    display: block;
    width: 100%;

    color: inherit;
    transition: var(--transition);

    &:hover,
    &:focus-visible {
      color: inherit;

      .title {
        color: var(--accent);
      }
    }
  }

  .title {
    margin: 0;
    color: var(--text-emphasis);
    font-size: var(--fz-xl);
    font-weight: 500;
    line-height: var(--lh-heading);
    transition: var(--transition);
  }

  /* Same construction as the blog header's meta line: one flex row, one
     gap, no punctuation between items. */

  .meta {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--space-2);
    margin-top: var(--space-0);
    color: var(--text-muted);
    font-size: var(--fz-md);

    > * + *::before {
      content: '·' / '';
      margin-right: var(--space-2);
      color: var(--text-disabled);
    }
  }

  .tags {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);

    &::before {
      margin-right: 0;
    }
  }

  .tag {
    &:hover,
    &:focus-visible {
      color: var(--accent);
    }
  }

  .description {
    max-width: var(--measure);
    margin: var(--space-0) 0 0;
    color: var(--text-body);
    font-size: var(--fz-md);
    line-height: var(--lh-body);
  }
`;

const formatDate = date =>
  new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const BlogList = ({ blogs, innerRef }) => (
  <List>
    {blogs.map(({ slug, title, date, tags, description }, i) => (
      <Row key={slug} ref={innerRef ? el => innerRef(el, i) : undefined}>
        <Link to={slug} className="inner">
          <h3 className="title">{title}</h3>
        </Link>
        <div className="meta">
          <time>{formatDate(date)}</time>
          {tags?.length > 0 && (
            <span className="tags">
              {tags.map(tag => (
                <Link key={tag} to={`/blogs?tag=${encodeURIComponent(tag)}`} className="tag">
                  #{tag}
                </Link>
              ))}
            </span>
          )}
        </div>
        {description ? <p className="description">{description}</p> : null}
      </Row>
    ))}
  </List>
);

BlogList.propTypes = {
  blogs: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      date: PropTypes.string,
      tags: PropTypes.array,
      description: PropTypes.string,
    }),
  ).isRequired,
  innerRef: PropTypes.func,
};

export default BlogList;
